import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useCreateOfferSection } from "@/hooks/use-offer-sections";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offerId: string;
  /** Sortering — settes høyere enn eksisterende seksjoner. */
  nextSortOrder: number;
}

/**
 * VIB-663e: dialog for å opprette en ny tilvalgsseksjon på et tilbud.
 * Eksempel: "Stillbildefotograf" som tilvalg i et videoproduksjons-tilbud.
 */
export function AddOfferSectionDialog({ open, onOpenChange, offerId, nextSortOrder }: Props) {
  const [name, setName] = useState("");
  const createSection = useCreateOfferSection();

  useEffect(() => {
    if (open) setName("");
  }, [open]);

  async function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Navn kan ikke være tomt");
      return;
    }
    try {
      await createSection.mutateAsync({
        offer_id: offerId,
        name: trimmed,
        section_type: "option",
        sort_order: nextSortOrder,
      });
      toast.success("Tilvalg lagt til");
      onOpenChange(false);
    } catch (err) {
      toast.error(`Kunne ikke opprette tilvalg: ${(err as Error).message}`);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nytt tilvalg</DialogTitle>
          <DialogDescription>
            Tilvalg vises som egen budsjettseksjon med eget navn, egne linjer og egen sum.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2 py-2">
          <Label htmlFor="section-name">Navn</Label>
          <Input
            id="section-name"
            placeholder="F.eks. Stillbildefotograf"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCreate();
              }
            }}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button onClick={handleCreate} disabled={createSection.isPending}>
            {createSection.isPending ? "Oppretter…" : "Legg til tilvalg"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
